import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';

export default class TotalRevenueHotel extends Model {
  [Type] = 'total-revenue/hotel' as const;

  @attr('number') declare capacity: number;
  @attr('hoteldate')
  declare firstCompleteStayDate: HoteldateTransformReturn;
  @attr('boolean') declare hasTransactionToReservationLink: boolean;
  @attr('hoteldate')
  declare lastCompleteStayDate: HoteldateTransformReturn;
  @attr declare name: string;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateCapacityForRange(from: Dayjs, to: Dayjs) {
    const numberOfNightsIntegrated = this.getNumberOfNightsIntegratedForRange(
      from,
      to,
    );

    return this.capacity * numberOfNightsIntegrated;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getNumberOfNightsIntegratedForRange(from: Dayjs, to: Dayjs) {
    if (!this.firstCompleteStayDate) {
      return 0;
    }

    // Hotel was integrated after the whole range
    if (this.firstCompleteStayDate.isAfter(to)) {
      return 0;
    }

    // Take the part of the range that is after the integration date
    const actualFrom = dayjs.max(this.firstCompleteStayDate, from);
    return to.diff(actualFrom, 'day') + 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getPeriodsWithoutData(from: Dayjs, to: Dayjs) {
    if (!this.firstCompleteStayDate || !this.lastCompleteStayDate) {
      return [{ from, to }];
    }

    if (
      this.firstCompleteStayDate.isAfter(to) ||
      this.lastCompleteStayDate.isBefore(from)
    ) {
      return [{ from, to }];
    }

    const periods = [];
    if (this.firstCompleteStayDate.isAfter(from)) {
      periods.push({ from, to: this.firstCompleteStayDate.subtract(1, 'day') });
    }

    if (this.lastCompleteStayDate.isBefore(to)) {
      periods.push({ from: this.lastCompleteStayDate.add(1, 'day'), to });
    }

    return periods;
  }
}
